/**
 * Shared movement logic for both client and server
 * This ensures identical physics calculations on both sides
 */

import type { IGameState, IPlayerState } from "./types";
import { GAME_CONFIG } from "./config";
import { hasWallBetween, getCell } from "./mazeUtils";

/**
 * Ensures that an entity's position is properly centered in a cell
 * and not clipping through walls. Used for both initial positioning
 * and correction of problematic positions.
 */
export function validateEntityPosition(
  gameState: IGameState,
  x: number,
  y: number,
  z = 0
): { x: number; y: number; z: number } {
  const maze = gameState.maze;
  if (!maze || maze.length === 0) {
    return { x, y, z };
  }

  // Get the current cell
  const cellX = Math.floor(x);
  const cellY = Math.floor(y);
  const currentCell = getCell(maze, cellX, cellY);

  // If we're not in a valid cell, find the nearest valid cell
  if (!currentCell) {
    const mazeSize = gameState.mazeSize || 20;
    // Clamp to maze boundaries
    const clampedX = Math.max(0, Math.min(mazeSize - 0.1, x));
    const clampedY = Math.max(0, Math.min(mazeSize - 0.1, y));
    return {
      x: clampedX,
      y: clampedY,
      z,
    };
  }

  // Calculate position within cell (0.0 to 1.0)
  const offsetX = x - cellX;
  const offsetY = y - cellY;

  // Check for wall proximity and correct if needed
  const wallProximityThreshold = 0.15; // Minimum distance from wall
  let adjustedX = x;
  let adjustedY = y;

  // Check north wall
  if (currentCell.walls[0] && offsetY < wallProximityThreshold) {
    adjustedY = cellY + wallProximityThreshold;
  }

  // Check east wall
  if (currentCell.walls[1] && offsetX > 1 - wallProximityThreshold) {
    adjustedX = cellX + (1 - wallProximityThreshold);
  }

  // Check south wall
  if (currentCell.walls[2] && offsetY > 1 - wallProximityThreshold) {
    adjustedY = cellY + (1 - wallProximityThreshold);
  }

  // Check west wall
  if (currentCell.walls[3] && offsetX < wallProximityThreshold) {
    adjustedX = cellX + wallProximityThreshold;
  }

  // Additional check for entity near cell borders
  // This prevents clipping when crossing cell boundaries
  if (offsetX < 0.1 || offsetX > 0.9 || offsetY < 0.1 || offsetY > 0.9) {
    // Check if we're near a cell boundary with a wall
    if (offsetX < 0.1) {
      // Near west boundary, check if west cell has an east wall
      const westCell = getCell(maze, cellX - 1, cellY);
      if (westCell?.walls[1]) {
        adjustedX = cellX + 0.1; // Push away from boundary
      }
    } else if (offsetX > 0.9) {
      // Near east boundary, check if east cell has a west wall
      const eastCell = getCell(maze, cellX + 1, cellY);
      if (eastCell?.walls[3]) {
        adjustedX = cellX + 0.9; // Pull back from boundary
      }
    }

    if (offsetY < 0.1) {
      // Near north boundary, check if north cell has a south wall
      const northCell = getCell(maze, cellX, cellY - 1);
      if (northCell?.walls[2]) {
        adjustedY = cellY + 0.1; // Push away from boundary
      }
    } else if (offsetY > 0.9) {
      // Near south boundary, check if south cell has a north wall
      const southCell = getCell(maze, cellX, cellY + 1);
      if (southCell?.walls[0]) {
        adjustedY = cellY + 0.9; // Pull back from boundary
      }
    }
  }

  return {
    x: adjustedX,
    y: adjustedY,
    z,
  };
}

/**
 * Calculate the next position for a player based on its current position and input
 * This function is used by both client and server to ensure consistent movement
 */
export function calculateNextPosition(
  gameState: IGameState,
  player: IPlayerState,
  direction: "up" | "down" | "left" | "right" | null,
  deltaTime = 1
): { x: number; y: number; valid: boolean; direction: number } {
  // No movement if player is respawning
  if (player.isRespawning) {
    return { x: player.x, y: player.y, valid: false, direction: -1 };
  }

  // Starting position
  let x = player.x;
  let y = player.y;
  let moved = false;
  let directionValue = -1; // 0: North, 1: East, 2: South, 3: West

  // Fixed movement speed - same on client and server
  // Make sure movement speed is reasonable - debug with this line
  // console.log("Movement speed:", GAME_CONFIG.PLAYER.MOVE_SPEED * deltaTime);
  const moveSpeed = GAME_CONFIG.PLAYER.MOVE_SPEED * Math.min(deltaTime, 2.0); // Cap deltaTime to prevent extreme speeds

  // Apply movement based on direction
  if (direction === "up") {
    y -= moveSpeed;
    moved = true;
    directionValue = 0; // North
  } else if (direction === "down") {
    y += moveSpeed;
    moved = true;
    directionValue = 2; // South
  } else if (direction === "left") {
    x -= moveSpeed;
    moved = true;
    directionValue = 3; // West
  } else if (direction === "right") {
    x += moveSpeed;
    moved = true;
    directionValue = 1; // East
  }

  // No movement requested
  if (!moved) {
    return { x: player.x, y: player.y, valid: false, direction: -1 };
  }

  // Check bounds with a small margin to prevent getting stuck at edges
  const mazeSize = gameState.mazeSize || 10;
  if (x < 0.05 || x >= mazeSize - 0.05 || y < 0.05 || y >= mazeSize - 0.05) {
    // Too close to bounds
    return {
      x: player.x,
      y: player.y,
      valid: false,
      direction: directionValue,
    };
  }

  // Check if there's a wall between current and next position
  if (hasWallBetween(gameState.maze, player.x, player.y, x, y)) {
    // Try to slide along the wall
    const slideX = direction === "left" || direction === "right" ? x : player.x;
    const slideY = direction === "up" || direction === "down" ? y : player.y;

    // If even sliding is blocked, abort the move
    if (hasWallBetween(gameState.maze, player.x, player.y, slideX, slideY)) {
      return {
        x: player.x,
        y: player.y,
        valid: false,
        direction: directionValue,
      };
    }

    // We can slide along the wall - make sure we're not too close to it
    const validatedPosition = validateEntityPosition(gameState, slideX, slideY);
    return {
      x: validatedPosition.x,
      y: validatedPosition.y,
      valid: true,
      direction: directionValue,
    };
  }

  // Validate final position to avoid wall clipping
  const validatedPosition = validateEntityPosition(gameState, x, y);

  // Movement is valid
  return {
    x: validatedPosition.x,
    y: validatedPosition.y,
    valid: true,
    direction: directionValue,
  };
}

/**
 * Validate if a move is legitimate (anti-cheat)
 */
export function isValidMove(
  gameState: IGameState,
  playerId: string,
  newX: number,
  newY: number
): boolean {
  const player = gameState.players[playerId];
  if (!player) return false;

  // Max distance a player could have moved in one update
  const maxDistance = GAME_CONFIG.ANTI_CHEAT.MAX_MOVE_DISTANCE * 1.5; // Allow some tolerance

  // Calculate actual distance moved
  const distance = Math.hypot(newX - player.x, newY - player.y);

  // Check if move is within allowed distance
  if (distance > maxDistance) {
    return false;
  }

  // Check if there are walls in the way
  if (hasWallBetween(gameState.maze, player.x, player.y, newX, newY)) {
    return false;
  }

  return true;
}
